import { ChangeDetectionStrategy, Component, computed, inject, Injector, input, linkedSignal, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { APPLICANT_MIN_MEETINGS, CommonButtonRowWidget, CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonSelectFieldWidget, UrlService, UtilityService, VOLUNTEER_MIN_MEETINGS } from "@eforall/common";
import { BlockoutSlots, Meetings, StandardFourTimeZoneId } from '@interfaces';
import { ScheduleTablePart } from '../schedule-table/schedule-table.part';
import { ScheduleService } from '../schedule.service';

@Component({
	selector: 'schedule-viewer-part',
	standalone: true,
	imports: [ScheduleTablePart, CommonSelectFieldWidget, CommonButtonRowWidget],
	providers: [ScheduleService], // Isolate edit mode and selected slots to this viewer and its schedule table.
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './schedule-viewer.part.html',
})

export class ScheduleViewerPart {

	public userType = input<'Applicant' | 'Volunteer' | undefined>(); // userType undefined is for accTeam
	public meetings = input.required<Meetings>();
	public blockoutSlots = input<BlockoutSlots[]>([]);
	public timeZoneId = input<StandardFourTimeZoneId | undefined>();
	public canEdit = input<boolean>(false);

	public saved = output<void>();

	private readonly util = inject(UtilityService);
	public service = inject(ScheduleService);

	public readonly form = new CommonForm(inject(Injector));
	public readonly commonLabels = inject(UrlService).commonLabels;

	public selectedTimeZoneId = linkedSignal(() => this.timeZoneId() ?? 1);
	public readonly minimumSelectionSlots = computed(() => this.userType() === 'Applicant' ? APPLICANT_MIN_MEETINGS : VOLUNTEER_MIN_MEETINGS);

	constructor() {
		toObservable(this.timeZoneIdField().pendingValue).subscribe((pendingValue: StandardFourTimeZoneId | null) => {
			if (pendingValue) {
				this.selectedTimeZoneId.set(pendingValue);
			}
		});
	}

	public lastUpdatedDate = computed(() => this.meetings()?.updatedUTC ? this.util.date.formatUTC(this.meetings().updatedUTC, 'MM/DD/YYYY', 'No Time', 'en-US') : 'N/A');

	public applicantTimeZone = computed(() => {
		const timeZoneId = this.timeZoneId();
		const timeZoneLabels = this.commonLabels.TimeZoneMap.long;
		return timeZoneId ? timeZoneLabels[timeZoneId]() : 'Not Provided';
	});

	private readonly timeZoneOptions = computed(() => {
		const timeZoneLabels = this.commonLabels.TimeZoneMap.long;
		return Object.entries(timeZoneLabels).map(([key, label]) => ({
			value: Number(key) as StandardFourTimeZoneId,
			text: label(),
		}));

	});

	readonly timeZoneIdField = computed<CommonSelectField<StandardFourTimeZoneId>>(() => ({
		config: signal<CommonOptionsFieldConfig<StandardFourTimeZoneId>>({
			label: 'Time Zone ',
			options: this.timeZoneOptions(),
			isRequired: true, isSpanish: false
		}),
		actualValue: computed(() => this.selectedTimeZoneId()),
		pendingValue: signal<StandardFourTimeZoneId | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'timeZoneId',
		form: this.form,
	}));

}